// COMP-FORM-RUNNER / annotated_image (US-DOC-150): نقطه روی تصویر یا نقشه بدن؛ value_json آرایه نقاط.
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo, useState, type MouseEvent } from 'react';

export interface AnnotatedImagePoint {
  x: number;
  y: number;
  area?: string | null;
  attrs?: Record<string, string>;
}

export interface AnnotatedImageFieldProps {
  valueJson?: string;
  onChange: (valueJson: string) => void;
  maxPoints?: number;
  backgroundLabel?: string;
  addLabel?: string;
  clearLabel?: string;
  testId?: string;
}

function parsePoints(valueJson?: string): AnnotatedImagePoint[] {
  if (!valueJson) {
    return [];
  }
  try {
    const parsed = JSON.parse(valueJson) as { points?: AnnotatedImagePoint[] } | AnnotatedImagePoint[];
    return Array.isArray(parsed) ? parsed : (parsed.points ?? []);
  } catch {
    return [];
  }
}

export function AnnotatedImageField({
  valueJson,
  onChange,
  maxPoints = 50,
  backgroundLabel = 'Image',
  addLabel = 'Add point',
  clearLabel = 'Clear',
  testId = 'annotated-image',
}: AnnotatedImageFieldProps) {
  const points = useMemo(() => parsePoints(valueJson), [valueJson]);
  const [draft, setDraft] = useState<AnnotatedImagePoint | null>(null);

  const commit = (next: AnnotatedImagePoint[]) => {
    onChange(JSON.stringify({ points: next.slice(0, maxPoints) }));
  };

  const click = (event: MouseEvent<SVGSVGElement>) => {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    if (points.length >= maxPoints) {
      return;
    }
    const point = { x: Number(x.toFixed(4)), y: Number(y.toFixed(4)) };
    setDraft(point);
    commit([...points, point]);
  };

  return (
    <Stack spacing={1} data-testid={testId}>
      <Typography variant="body2">{backgroundLabel}</Typography>
      <Box
        component="svg"
        viewBox="0 0 100 100"
        onClick={click}
        sx={{ width: '100%', maxWidth: 360, aspectRatio: '1', bgcolor: 'action.hover', cursor: 'crosshair', borderRadius: 1 }}
        role="img"
        aria-label={backgroundLabel}
      >
        <rect width="100" height="100" fill="transparent" />
        {points.map((point, index) => (
          <circle key={`${point.x}-${point.y}-${index}`} cx={point.x * 100} cy={point.y * 100} r={2.5} fill="currentColor" />
        ))}
        {draft ? <circle cx={draft.x * 100} cy={draft.y * 100} r={3} fill="primary" opacity={0.5} /> : null}
      </Box>
      <Stack direction="row" spacing={1}>
        <Button size="small" disabled={points.length >= maxPoints} onClick={() => commit([...points, { x: 0.5, y: 0.5 }])}>
          {addLabel}
        </Button>
        <Button size="small" onClick={() => commit([])}>
          {clearLabel}
        </Button>
        <Typography variant="caption">{points.length}/{maxPoints}</Typography>
      </Stack>
    </Stack>
  );
}
